import { Building2, CheckCircle2, Clock, Phone, PhoneCall, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { TabsContent } from "@/shared/components/ui/tabs";
import { displayValue } from "@/modules/hrPortal/utils/formatters";
import { getVoiceCallStatusColor } from "@/modules/hrPortal/utils/voiceCallHelpers";
import type { VoiceCallLog } from "@/modules/hrPortal/types";

interface DashboardStats {
  totalApplications: number;
  pendingATS: number;
  selected: number;
  selectionRate: number;
  voiceCallsToday?: number | null;
  voiceCallSuccessRate?: number | null;
  todayApplications: number;
  interviews?: number | null;
  voiceCallsWeek?: number | null;
  departmentHiring: Array<{
    name: string;
    count: number;
    percentage: number;
    color: string;
  }>;
}

interface DashboardTabProps {
  dashboardStats: DashboardStats;
  voiceCallLogs: VoiceCallLog[];
  onCallSelect: (call: VoiceCallLog) => void;
}

export default function DashboardTab({
  dashboardStats,
  voiceCallLogs,
  onCallSelect,
}: DashboardTabProps) {
  return (
    <TabsContent value="dashboard">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Applications</p>
                <p className="text-3xl font-bold">{dashboardStats.totalApplications}</p>
                <p className="mt-1 text-xs text-muted-foreground">All time</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending ATS</p>
                <p className="text-3xl font-bold">{dashboardStats.pendingATS}</p>
                <p className="mt-1 text-xs text-yellow-600">Awaiting screening</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Selected</p>
                <p className="text-3xl font-bold">{dashboardStats.selected}</p>
                <p className="mt-1 text-xs text-green-600">{dashboardStats.selectionRate}% rate</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Voice Calls Today</p>
                <p className="text-3xl font-bold">{dashboardStats.voiceCallsToday ?? 0}</p>
                <p className="mt-1 text-xs text-purple-600">
                  {dashboardStats.voiceCallSuccessRate ?? 0}% success
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                <PhoneCall className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <p className="text-sm text-blue-100">Today's Applications</p>
            <p className="text-4xl font-bold">{dashboardStats.todayApplications}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <p className="text-sm text-green-100">Interviews Scheduled</p>
            <p className="text-4xl font-bold">{displayValue(dashboardStats.interviews)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <p className="text-sm text-purple-100">Voice Calls This Week</p>
            <p className="text-4xl font-bold">{dashboardStats.voiceCallsWeek ?? 0}</p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Department-wise Hiring
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboardStats.departmentHiring.map((dept) => (
                <div key={dept.name} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{dept.name}</span>
                    <span className="font-medium">{dept.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100">
                    <div
                      className={`h-2 rounded-full ${dept.color}`}
                      style={{ width: `${dept.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PhoneCall className="h-5 w-5 text-purple-600" />
              Recent Voice Calls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {voiceCallLogs.slice(0, 4).map((call) => (
                <div
                  key={call.id}
                  className="flex cursor-pointer items-center justify-between rounded-lg bg-slate-50 p-3 hover:bg-slate-100"
                  onClick={() => onCallSelect(call)}
                >
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4" />
                    <div>
                      <p className="font-medium">{call.candidateName}</p>
                      <p className="text-xs text-muted-foreground">{call.duration}</p>
                    </div>
                  </div>
                  <Badge
                    variant="secondary"
                    className={getVoiceCallStatusColor(call.status ?? "")}
                  >
                    {call.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </TabsContent>
  );
}
